import React, { useEffect, useState } from "react";
import cn from "classnames";
import styles from "../styles.modules.scss";
import { FormattedMessage } from "react-intl";
import { Sparkles } from "../../../content-card/sparkles";
import { Button } from "../../../button";
import { Image } from "../../../image";
import { useInboxRewardsContext } from "../../../../stores/inboxRewards";
import { useElementOnScreen } from "../../../../hooks/use-element-on-screen";
import { useSendCommand } from "../../../../hooks/use-send-command";
import { useOpenNewsArticle } from "../../../../hooks/use-open-news-article";
import { handleCTAClick } from "..";
import { parseCTAs } from "../parse-ctas";
import { usePollContext } from "../../../../stores/poll";
import { openShopOffer } from "../../../../utils/legacy-ingame-commands";
import { GameName } from "inbox-contentful";
import { Timer } from "../default/timer";

export type ProgressSlideType = {
    type: "progressTrackerEntry";
    id: string;
    background?: ImageSourceSets;
    title: string;
    tracker: ProgressTracker;
    milestones: ProgressMilestone[];
    ctas: InboxCTA[];
    targeting?: Targeting;
    lang: Locale;
    relatedMedia?: RelatedMedia[];
};

const DO_SPARKLE_EFFECT = window.GAMENAME === "clashofclans";

export const ZippyZebraProgressSlide = ({
    slide,
}: {
    slide: ProgressSlideType;
}) => {
    const { title, tracker, milestones, background, targeting, relatedMedia } =
        slide;

    const { actions } = useInboxRewardsContext();

    const claimedVouchers = actions.getClaimedVouchers();

    const [ref, isVisible] = useElementOnScreen<HTMLDivElement>();
    const sendCommand = useSendCommand();
    const openNewsArticle = useOpenNewsArticle();
    const {
        actions: { openModal },
    } = usePollContext();

    const handleClick = handleCTAClick(
        sendCommand,
        openNewsArticle,
        openModal,
        title,
    );

    const ctas = parseCTAs(slide.ctas, handleClick);

    const fillPercentage = tracker.progress;
    const direction = tracker.direction;
    const barColor = tracker.color;

    useEffect(() => {
        const availableVouchers = milestones.filter((m) => !!m.voucherId);

        if (!availableVouchers.length) {
            return;
        }

        const voucherIds = availableVouchers.map(
            (m) => m.voucherId,
        ) as string[];

        actions.fetchUnclaimedVouchers(voucherIds);
    }, []);

    const style =
        direction === "vertical"
            ? {
                  height: `${fillPercentage}%`,
              }
            : {
                  width: `${fillPercentage}%`,
              };

    const hasMileStoneLabels = milestones.some(
        (milestone) => !!milestone.label,
    );

    const barColorStyle = { "--bar-color": barColor } as React.CSSProperties;

    // Moco can use a related media "timer" label to show the end time of the progress tracker.
    const timerLabel = relatedMedia?.find(
        (media) => media.title === "timer",
    )?.label;

    const [endTime, setEndTime] = useState(() => {
        const endTime = new Date(timerLabel ?? 0).getTime() - Date.now();
        return endTime;
    });

    useEffect(() => {
        if (window.GAMENAME !== "rogue" || !timerLabel) return;

        const interval = setInterval(() => {
            setEndTime((prevEndTime) => prevEndTime - 1000);
        }, 1000);

        return () => clearInterval(interval);
    }, []);

    return (
        <div
            className={`slide progress-indicator ${styles.slide} ${
                isVisible ? "visible" : ""
            } ${hasMileStoneLabels ? "with-milestone-labels" : ""} ${
                targeting?.campaignId ?? ""
            } ${title ? "with-title" : "without-title"}`}
            ref={ref}
            style={barColorStyle}
        >
            <div className={`content ${styles.content}`}>
                {window.GAMENAME === "rogue" && timerLabel && (
                    <>
                        <Timer format="total-hours" countdown={endTime} />
                        <div className="timer-spacer" />
                    </>
                )}
                {title && (
                    <>
                        <div className={`filter ${styles.filter}`} />

                        <div
                            className={`title ${styles.title} `}
                            data-title={title}
                        >
                            {title}
                        </div>
                    </>
                )}

                {background && (
                    <div className={`image ${styles.image}`}>
                        <Image sources={background} cover />
                    </div>
                )}

                <div className={`trackers ${styles.trackers}`}>
                    {isVisible && (
                        <div className={`tracker-wrapper`}>
                            <div className={`tracker ${styles.tracker}`}>
                                <div
                                    style={style}
                                    className={`tracker-progress ${
                                        styles["tracker-progress"]
                                    } ${
                                        fillPercentage > 99
                                            ? "almost-complete"
                                            : ""
                                    } `}
                                />
                            </div>

                            {milestones.map((milestone, index) => {
                                const milestonePosition = Math.max(
                                    milestone.progress,
                                    5, // 5% is the minimum we support without the UI looking totally wack
                                );

                                const style =
                                    direction === "vertical"
                                        ? {
                                              bottom: `${milestonePosition}%`,
                                          }
                                        : {
                                              left: `${milestonePosition}%`,
                                          };

                                return (
                                    <span
                                        key={index}
                                        style={style}
                                        className="milestone-marker"
                                    />
                                );
                            })}
                        </div>
                    )}

                    <div
                        className={`milestone-wrapper ${styles["milestone-wrapper"]} milestones-${milestones.length}`}
                    >
                        {milestones.map((milestone, index) => {
                            if (!milestone.image) {
                                return null;
                            }

                            const milestonePosition = Math.max(
                                milestone.progress,
                                5, // 5% is the minimum we support without the UI looking totally wack
                            );

                            const hasReachedMilestone = milestone.unlocked;

                            const style =
                                direction === "vertical"
                                    ? {
                                          bottom: `${milestonePosition}%`,
                                      }
                                    : {
                                          left: `${milestonePosition}%`,
                                      };

                            const isClaimable = !!milestone.voucherId;

                            const isClaimed = claimedVouchers.includes(
                                milestone.voucherId as string,
                            );

                            return (
                                <div
                                    onClick={() => {
                                        if (isClaimed) {
                                            return;
                                        }

                                        if (
                                            hasReachedMilestone &&
                                            milestone.eventId
                                        ) {
                                            openShopOffer({
                                                game: window.GAMENAME as GameName,
                                                eventId: milestone.eventId,
                                                sendCommand,
                                            });
                                        }

                                        if (!milestone.voucherId) {
                                            return;
                                        }

                                        actions.claimVoucher(
                                            milestone.voucherId,
                                        );
                                    }}
                                    style={style}
                                    key={index}
                                    className={cn(
                                        "milestone-img",
                                        styles["milestone-img"],
                                        {
                                            "milestone-achieved": isClaimable
                                                ? hasReachedMilestone &&
                                                  isClaimed
                                                : hasReachedMilestone,
                                        },

                                        {
                                            "milestone-unachieved":
                                                !hasReachedMilestone,
                                        },

                                        isClaimable
                                            ? hasReachedMilestone && isClaimed
                                            : hasReachedMilestone,
                                        {
                                            "milestone-claimable":
                                                !isClaimed && isClaimable,
                                        },

                                        !isClaimed && isClaimable
                                            ? styles["milestone-claimable"]
                                            : undefined,
                                    )}
                                >
                                    {DO_SPARKLE_EFFECT ? (
                                        <Sparkles
                                            sparkleSize={{
                                                min: 5,
                                                max: 10,
                                            }}
                                            sparkleArea={{
                                                top: -80,
                                                bottom: 80,
                                                left: -80,
                                                right: 80,
                                            }}
                                            color="#fff"
                                            sparkleAmount={2}
                                            active={hasReachedMilestone}
                                        >
                                            <Image sources={milestone.image} />
                                        </Sparkles>
                                    ) : (
                                        <Image sources={milestone.image} />
                                    )}

                                    {index === 1 && (
                                        <div className="day-title">
                                            {milestone.label}
                                        </div>
                                    )}

                                    {!hasReachedMilestone && (
                                        <div className="milestone-locked" />
                                    )}
                                    {hasReachedMilestone && (
                                        <div className="milestone-unlocked" />
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    <div
                        className={`milestone-label-wrapper ${styles["milestone-label-wrapper"]}`}
                    >
                        {milestones.map((milestone, index) => {
                            if (!milestone.label) {
                                return null;
                            }

                            const milestonePosition = Math.max(
                                milestone.progress,
                                5, // 5% is the minimum we support without the UI looking totally wack
                            );

                            const hasReachedMilestone = milestone.unlocked;

                            const style =
                                direction === "vertical"
                                    ? {
                                          bottom: `${milestonePosition}%`,
                                      }
                                    : {
                                          left: `${milestonePosition}%`,
                                      };

                            return (
                                <div
                                    key={index}
                                    style={style}
                                    className={`milestone-label ${
                                        styles["milestone-label"]
                                    } ${
                                        hasReachedMilestone
                                            ? styles["milestone-reached"]
                                            : styles["milestone-unreached"]
                                    } ${
                                        hasReachedMilestone
                                            ? "milestone-reached"
                                            : "milestone-unreached"
                                    }`}
                                    title={milestone.label}
                                >
                                    {milestone.label}

                                    {hasReachedMilestone && (
                                        <div className="milestone-unlocked" />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className={`wrapper ${styles.wrapper}`}>
                    {ctas.length ? (
                        <div
                            className={`ctas ${
                                ctas.length === 1 ? "single" : "multi"
                            } ${styles.ctas}`}
                        >
                            {ctas.map(({ label, variant, onClick }, index) => {
                                return (
                                    <Button
                                        key={index}
                                        onClick={onClick}
                                        variant={variant}
                                    >
                                        <span className="buttonTextWrapper">
                                            {label || (
                                                <FormattedMessage id="globalText.langGo" />
                                            )}
                                        </span>
                                    </Button>
                                );
                            })}
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
};
